import Vue from 'vue';
import { ApolloClient, createHttpLink, from } from '@apollo/client/core';
import { RetryLink } from '@apollo/client/link/retry';
import { setContext } from '@apollo/client/link/context';
import { InMemoryCache } from '@apollo/client/cache';
import VueApollo from 'vue-apollo';
import { readMeta } from './page';
import { refreshedJwt } from './jwt';

let sharedApolloProvider: VueApollo | null = null;

export function setupApollo(includeJWT = true, defaultOptions = {} as Object) {
  if (sharedApolloProvider) {
    return sharedApolloProvider;
  }

  // Apollo/Graph QL setup
  const gqlURI = readMeta('graphql');
  if (!gqlURI) {
    throw new Error('GQL endpoint not specified');
  }

  const httpLink = createHttpLink({ uri: gqlURI });

  const retryLink = new RetryLink({
    delay: {
      initial: 300,
      max: 5000,
      jitter: true,
    },
    attempts: {
      max: 3,
      retryIf: (error) => !!error,
    },
  });

  const authLink = setContext(async () => {
    // don't include the jwt if the call doesn't need it
    if (!includeJWT) return {};

    const token = await refreshedJwt();

    // don't include the jwt if there was no jwt to include
    if (!token) return {};

    // finally, if the token needs refreshing, do that and then return the new bearer token header
    return { headers: { authorization: `Bearer ${token}` } };
  });

  const additiveLinks = from([authLink, retryLink, httpLink]);

  const client = new ApolloClient({
    link: additiveLinks,
    cache: new InMemoryCache(),
    defaultOptions,
  });

  sharedApolloProvider = new VueApollo({ defaultClient: client });

  // Install vue plugin
  Vue.use(VueApollo);

  return sharedApolloProvider;
}

/**
 * Get the shared Apollo client instance used by the SPA.
 * This ensures all guards use the same client and cache.
 */
export function getSharedApolloClient() {
  if (!sharedApolloProvider) {
    throw new Error('Apollo provider not initialized. Call setupApollo() first.');
  }
  return sharedApolloProvider.defaultClient;
}

export default {
  setupApollo,
};
